/**
 * dev-grant.ts — pure resource-grant helper for the v3M13 Dev Toolbox.
 *
 * Adds a fixed amount to the player empire's resources, clamped at the
 * appropriate cap so we don't blow through the storage / Diplomatic
 * ceilings. Research has no cap (flows into the active research project).
 *
 * MULTIPLAYER §2 caveat: this directly mutates GameState — single-player
 * cheat path only. v3M9 multiplayer will need a command path with
 * authoritative-server arbitration. v1 single-player keeps it simple.
 */

import type { GameState } from '../state/types';
import { diplomaticCap } from './balance-structures';
import { resourceCapsForEmpire } from './planet-economy';

/** Default grant amount (per click of a single-resource button). */
export const DEV_GRANT_AMOUNT = 10000;

/** Resources the toolbox can grant. */
export type GrantableResource =
  | 'credits'
  | 'minerals'
  | 'fuel'
  | 'research'
  | 'diplomatic';

/**
 * Add `amount` of `resource` to the player empire, clamping at the
 * appropriate cap. No-op if the player empire is missing.
 *
 * Caps:
 *   - credits / minerals / fuel: storage caps from `resourceCapsForEmpire`.
 *   - research: uncapped.
 *   - diplomatic: `DIPLOMATIC_CAP` (1000).
 */
export function grantResourceToPlayer(
  state: GameState,
  resource: GrantableResource,
  amount: number = DEV_GRANT_AMOUNT,
): void {
  const empire = state.empires.get(state.playerEmpireId);
  if (!empire) return;
  if (amount <= 0) return;

  if (resource === 'credits' || resource === 'minerals' || resource === 'fuel') {
    const caps = resourceCapsForEmpire(state, state.playerEmpireId);
    const cap = caps[resource];
    empire.resources[resource] = Math.min(cap, empire.resources[resource] + amount);
    return;
  }
  if (resource === 'research') {
    empire.resources.research = empire.resources.research + amount;
    return;
  }
  if (resource === 'diplomatic') {
    empire.resources.diplomatic = Math.min(
      diplomaticCap(state.structuresData),
      empire.resources.diplomatic + amount,
    );
    return;
  }
}

/**
 * Grant `amount` of every grantable resource at once. Convenience wrapper
 * for the toolbox's "All +10k" button.
 */
export function grantAllResourcesToPlayer(
  state: GameState,
  amount: number = DEV_GRANT_AMOUNT,
): void {
  grantResourceToPlayer(state, 'credits', amount);
  grantResourceToPlayer(state, 'minerals', amount);
  grantResourceToPlayer(state, 'fuel', amount);
  grantResourceToPlayer(state, 'research', amount);
  grantResourceToPlayer(state, 'diplomatic', amount);
}
